import { InfiniteData } from '@tanstack/react-query';
import {
  CreateTaskDto,
  PriorityLevel,
  TaskDto,
  TaskListDto,
  UpdateTaskDto,
} from '@sib/api';
import {
  buildQueryArraySorters,
  buildQueryFilters,
  getBackendTimestamp,
  getTodayEnd,
  getTodayStart,
} from '@sib/api/utils';
import { CrudFilters } from '@sib/types';
import { subMinutes, startOfWeek, endOfWeek } from 'date-fns';
import { produce } from 'immer';

export interface GetTasksSearchParams {
  searchTerm?: string;
}

export type TasksKeys =
  | keyof CreateTaskDto
  | keyof TaskDto
  | keyof UpdateTaskDto;

export const TASK_RESOURCE_NAME = 'task';
export const TASK_DESCRIPTION_MAX_LENGTH = 4096;
export const TASK_TITLE_MAX_LENGTH = 256;
export const TASK_COMMENT_MAX_LENGTH = 4096;
export const TASK_REMINDER_MAX_LENGTH = 200;

export const TaskResourceField = {
  UUID: 'uuid',
  TITLE: 'title',
  DESCRIPTION: 'description',
  PRIORITY_LEVEL: 'priorityLevel',
  DEADLINE: 'deadline',
  COMPLETED: 'completed',
  CATEGORY: 'category',
  CREATED_AT: 'createdAt',
  REFERENCE_NAME: 'referenceName',
  REFERENCE_LINK: 'referenceLink',
  COMPLETED_AT: 'completedAt',
  SEEN: 'seen',
} as const satisfies Record<
  string,
  keyof CreateTaskDto | keyof TaskDto | keyof UpdateTaskDto
>;

export const activeTasksFilters: CrudFilters = [
  {
    field: TaskResourceField.COMPLETED,
    operator: 'eq',
    value: false,
  },
];

export const archivedBaseDashboardFilters: CrudFilters = [
  {
    field: TaskResourceField.COMPLETED,
    operator: 'eq',
    value: true,
  },
];

export const getArchivedTodayFilters = () =>
  buildQueryFilters([
    ...archivedBaseDashboardFilters,
    {
      field: TaskResourceField.DEADLINE,
      operator: 'between',
      value: [
        getBackendTimestamp(getTodayStart()),
        getBackendTimestamp(getTodayEnd()),
      ],
    },
  ]);

export const getTodaySectionFilters = () =>
  buildQueryFilters([
    ...activeTasksFilters,
    {
      field: TaskResourceField.DEADLINE,
      operator: 'between',
      value: [
        getBackendTimestamp(getTodayStart()),
        getBackendTimestamp(getTodayEnd()),
      ],
    },
  ]);

export const getDueThisWeekSectionFilters = () =>
  buildQueryFilters([
    ...activeTasksFilters,
    {
      field: TaskResourceField.DEADLINE,
      operator: 'between',
      value: [
        getBackendTimestamp(startOfWeek(new Date())),
        getBackendTimestamp(endOfWeek(new Date())),
      ],
    },
  ]);

export const getArchivedUpcomingFilters = () =>
  buildQueryFilters([
    ...archivedBaseDashboardFilters,
    {
      field: TaskResourceField.DEADLINE,
      operator: 'gt',
      value: getBackendTimestamp(getTodayEnd()),
    },
  ]);

export const getUpcomingSectionFilters = () =>
  buildQueryFilters([
    ...activeTasksFilters,
    {
      field: TaskResourceField.DEADLINE,
      operator: 'gt',
      value: getBackendTimestamp(getTodayEnd()),
    },
  ]);

export const getArchivedOverdueFilters = () =>
  buildQueryFilters([
    ...archivedBaseDashboardFilters,
    {
      field: TaskResourceField.DEADLINE,
      operator: 'lt',
      value: getBackendTimestamp(getTodayStart()),
    },
  ]);

export const getOverdueSectionFilters = () =>
  buildQueryFilters([
    ...activeTasksFilters,
    {
      field: TaskResourceField.DEADLINE,
      operator: 'lt',
      value: getBackendTimestamp(getTodayStart()),
    },
  ]);

export const getPrioritySectionFilters = (priority: PriorityLevel): string[] =>
  buildQueryFilters([
    {
      field: TaskResourceField.PRIORITY_LEVEL,
      operator: 'eq',
      value: priority,
    },
  ]);

export const getRecentlyClosedSectionFilters = () =>
  buildQueryFilters([
    {
      field: TaskResourceField.COMPLETED,
      operator: 'eq',
      value: true,
    },
    {
      field: TaskResourceField.COMPLETED_AT,
      operator: 'gt',
      value: getBackendTimestamp(subMinutes(new Date(), 10)),
    },
  ]);

export const archiveSorters = buildQueryArraySorters([
  {
    field: TaskResourceField.COMPLETED_AT,
    order: 'desc',
  },
  {
    field: TaskResourceField.PRIORITY_LEVEL,
    order: 'desc',
  },
  {
    field: TaskResourceField.CREATED_AT,
    order: 'desc',
  },
]);

export const taskListSorters = buildQueryArraySorters([
  {
    field: TaskResourceField.SEEN,
    order: 'asc',
  },
  {
    field: TaskResourceField.DEADLINE,
    order: 'asc',
  },
  {
    field: TaskResourceField.PRIORITY_LEVEL,
    order: 'desc',
  },
  {
    field: TaskResourceField.CREATED_AT,
    order: 'desc',
  },
]);

export const activeTasksSorters = buildQueryArraySorters([
  {
    field: TaskResourceField.COMPLETED,
    order: 'asc',
  },
]);

export const archivedSectionFilters = buildQueryFilters([
  {
    field: TaskResourceField.COMPLETED,
    operator: 'eq',
    value: true,
  },
]);

// TODO unit tests
export const handleOptimisticAddTask = (
  newData: TaskDto,
  prevData: InfiniteData<TaskListDto> | TaskListDto
) =>
  produce(prevData, draftData => {
    if ('pages' in draftData) {
      draftData.pages[0].items = [newData, ...draftData.pages[0].items];
      draftData.pages[0].totalItems += 1;
      const { newItemsCount } = draftData.pages[0];

      if (typeof newItemsCount === 'number')
        draftData.pages[0].newItemsCount = newItemsCount + 1;

      return draftData;
    }

    draftData.totalItems += 1;

    if (typeof draftData.newItemsCount === 'number')
      draftData.newItemsCount = draftData.newItemsCount + 1;

    return draftData;
  });

export const handleOptimisticUpdateTask = (
  newData: Partial<TaskDto>,
  prevData: InfiniteData<TaskListDto> | TaskListDto
) =>
  produce(prevData, draftData => {
    if ('pages' in draftData) {
      const pageIndex = draftData.pages.findIndex(p =>
        p.items.some(i => i.uuid === newData.uuid)
      );

      const page = draftData.pages[pageIndex];

      if (page) {
        const index = page.items.findIndex(i => i.uuid === newData.uuid);
        const currentItem = draftData.pages[pageIndex].items[index];

        const changedFromUnseenToSeen =
          newData.seen && !currentItem.seen && !currentItem.seenAt;

        draftData.pages[pageIndex].items[index] = {
          ...page.items[index],
          ...newData,
          ...(newData.seen && {
            seen: true,
            seenAt: newData.seenAt ?? getBackendTimestamp(new Date()),
          }),
        };
        if (
          changedFromUnseenToSeen &&
          typeof draftData.pages[0].newItemsCount === 'number'
        )
          draftData.pages[0].newItemsCount -= 1;
      }

      return draftData;
    }

    return draftData;
  });
